import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

const ImportSummary = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const importResult = location.state?.importResult || {};
  // returnState holds selectedEntity, selectedActivity, selectedName
  // so Import page can restore them when user clicks "← Import Another File"
  const returnState  = location.state?.returnState  || {};

  const validCount = importResult.valid_count || 0;
  const errorCount = importResult.error_count || 0;
  const filename   = importResult.filename    || "";
  const errors     = importResult.errors      || [];
  const hasErrors  = errors.length > 0;

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .is-page {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #f5f5f5;
          min-height: 100vh;
          padding: 32px 40px;
          color: #111;
        }
        .is-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 28px;
        }
        .is-breadcrumb {
          font-size: 11px;
          font-weight: 600;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          margin-bottom: 4px;
        }
        .is-title {
          font-size: 20px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 4px;
        }
        .is-subtitle { font-size: 13px; color: #6B7280; }
        .is-subtitle strong { font-weight: 600; color: #374151; }
        .is-cancel-btn {
          background: #fff;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          padding: 7px 18px;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          white-space: nowrap;
        }
        .is-cancel-btn:hover { background: #F9FAFB; }
        .is-stats-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }
        .is-stat-card {
          background: #fff;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .is-stat-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 16px;
        }
        .is-stat-icon--success { background: #ECFDF5; border: 2px solid #10B981; color: #10B981; }
        .is-stat-icon--error   { background: #FEF2F2; border: 2px solid #EF4444; color: #EF4444; }
        .is-stat-num   { font-size: 28px; font-weight: 700; color: #111827; line-height: 1; margin-bottom: 4px; }
        .is-stat-label { font-size: 11px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.06em; }
        .is-errors-card {
          background: #fff;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 24px;
        }
        .is-errors-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 20px;
          border-bottom: 1px solid #E5E7EB;
          background: #fff;
        }
        .is-errors-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #111827;
        }
        .is-errors-dot {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #FEE2E2;
          border: 2px solid #EF4444;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .is-errors-dot::after { content: ''; width: 5px; height: 5px; border-radius: 50%; background: #EF4444; }
        .is-issues-badge {
          background: #FEE2E2;
          color: #DC2626;
          font-size: 11px;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 999px;
          border: 1px solid #FECACA;
        }
        .is-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .is-table thead tr { background: #F9FAFB; }
        .is-table th {
          padding: 10px 20px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #E5E7EB;
        }
        .is-table td {
          padding: 14px 20px;
          color: #374151;
          border-bottom: 1px solid #F3F4F6;
          font-size: 13px;
        }
        .is-table tbody tr:last-child td { border-bottom: none; }
        .is-table tbody tr:hover td { background: #FAFAFA; }
        .is-footer { display: flex; gap: 12px; margin-top: 8px; }
        .is-btn-secondary {
          background: #fff;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          padding: 8px 18px;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
        }
        .is-btn-secondary:hover { background: #F9FAFB; }
      `}</style>

      <div className="is-page">

        {/* Header */}
        <div className="is-header">
          <div>
            <div className="is-breadcrumb">Environment &amp; Carbon</div>
            <div className="is-title">Import Summary</div>
            <div className="is-subtitle">
              Validation results for <strong>{filename}</strong>
            </div>
          </div>
          <button className="is-cancel-btn" onClick={() => navigate(-1)}>Cancel</button>
        </div>

        {/* Stat Cards */}
        <div className="is-stats-row">
          <div className="is-stat-card">
            <div className="is-stat-icon is-stat-icon--success">✓</div>
            <div>
              <div className="is-stat-num">{validCount}</div>
              <div className="is-stat-label">Rows Valid</div>
            </div>
          </div>
          <div className="is-stat-card">
            <div className="is-stat-icon is-stat-icon--error">✕</div>
            <div>
              <div className="is-stat-num">{errorCount}</div>
              <div className="is-stat-label">Rows Failed</div>
            </div>
          </div>
        </div>

        {/* Errors Table */}
        {hasErrors && (
          <div className="is-errors-card">
            <div className="is-errors-header">
              <div className="is-errors-title">
                <div className="is-errors-dot" />
                Errors
              </div>
              <span className="is-issues-badge">{errors.length} issues</span>
            </div>
            <table className="is-table">
              <thead>
                <tr>
                  <th style={{ width: "160px" }}>Row Number</th>
                  <th>Error Message</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((err, i) => (
                  <tr key={i}>
                    <td>{err.row ?? i + 1}</td>
                    <td>{JSON.stringify(err.error)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="is-footer">
          <button
            className="is-btn-secondary"
            onClick={() =>
              // Pass returnState back so Import page restores entity + activity
              navigate("/dashboard/import", { state: { returnState } })
            }
          >
            ← Import Another File
          </button>
        </div>

      </div>
    </>
  );
};

export default ImportSummary;